<template>
    <div class="edit-profile-modal">
        <el-dialog
            :model-value="modelValue"
            width="1200px"
            :show-close="false"
            class="custom-modal"
            :destroy-on-close="true"
            @close="close"
            @closed="onClosed"
        >
            <template #header="{ close }">
                <div class="custom-modal-header">
                    <div class="profile-summary-info">
                        <div class="position-wrapper">
                            <img
                                :src="`/media/images/web_skill_ranking/numbers-${String(profile?.current_position).padStart(2, '0')}.svg`"
                                alt="Position"
                                class="position-svg"
                            />
                        </div>

                        <div
                            class="avatar-wrapper"
                            :style="{
                                backgroundColor: avatarBackground(profile?.id),
                            }"
                        >
                            <img
                                v-if="profile?.photo"
                                :src="profile.photo"
                                class="profile-avatar"
                                alt="Avatar"
                            />
                        </div>

                        <div class="cell-multiline">
                            <div class="font-bold">
                                {{ profile?.profile_name || "-" }}
                            </div>

                            <div class="text-sm mt-1">
                                {{ profile?.address || "-" }}
                            </div>
                        </div>
                    </div>
                    <div class="cancel-area">
                        <span class="dialog-cancel-text" @click="close">
                            {{ $trans("web_skill_ranking.cancel") || "Cancel" }}
                            <el-icon><Close /></el-icon>
                        </span>
                    </div>
                    <div class="header-actions">
                        <el-button
                            type="primary"
                            @click="save"
                            :disabled="!isValid"
                            class="save-button"
                        >
                            {{ $trans("web_skill_ranking.save") || "Save" }}
                        </el-button>
                    </div>
                </div>
            </template>

            <div class="content-container">
                <div class="field-group">
                    <label class="field-label">
                        {{
                            $trans("web_skill_ranking.admin.headline") ||
                            "Headline"
                        }}
                    </label>

                    <el-input
                        id="claimTitle"
                        v-model="localHeadline"
                        :placeholder="
                            $trans('web_skill_ranking.admin.enter_headline') ||
                            'Enter headline'
                        "
                        type="textarea"
                        :rows="2"
                        maxlength="100"
                        resize="vertical"
                    />

                    <div class="char-counter">
                        <span>{{ localHeadline.length }}/100</span>&nbsp;
                        <span>
                            {{
                                $trans("web_skill_ranking.characters_used") ||
                                "characters used"
                            }}
                        </span>
                    </div>
                </div>

                <div class="field-group">
                    <label class="field-label">
                        {{
                            $trans("web_skill_ranking.admin.summary") ||
                            "Summary"
                        }}
                    </label>

                    <Editor
                        ref="myTinyEditor"
                        :key="profile?.id || 'editor'"
                        v-model="localSummary"
                        :init="tinyInit"
                        :tinymce-script-src="cdnSrc"
                        license-key="gpl"
                    />

                    <div class="char-counter">
                        <span>{{ safeContentLength }}/5000</span>&nbsp;
                        <span>
                            {{
                                $trans("web_skill_ranking.characters_used") ||
                                "characters used"
                            }}
                        </span>
                    </div>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { Close } from "@element-plus/icons-vue";
import Editor from "@tinymce/tinymce-vue";

export default {
    name: "EditProfileSkillRankingModal",

    components: {
        Close,
        Editor,
    },

    props: {
        modelValue: {
            type: Boolean,
            required: true,
        },
        profile: {
            type: Object,
            required: true,
        },
    },

    emits: ["update:modelValue", "submit"],

    data() {
        return {
            localHeadline: "",
            localSummary: "",
            previousSummary: "",

            cdnSrc: "/vendor/tinymce/tinymce.min.js",

            tinyInit: {
                height: 300,
                menubar: false,
                branding: false,
                promotion: false,
                base_url: "/vendor/tinymce",
                suffix: ".min",
                license_key: 'gpl',
                plugins: "lists link autoresize code",
                toolbar:
                    "undo redo | bold italic underline | bullist numlist | link | code | removeformat",
                default_link_target: "_blank",
                link_assume_external_targets: true,
                entity_encoding: "raw",
                content_style:
                    "body { background: #fff; font-family: Barlow, sans-serif; font-size: 15px; font-weight: 700; }",
            },
        };
    },

    computed: {
        safeContentLength() {
            return this.getPlainText(this.localSummary).length;
        },

        isValid() {
            return (
                this.localHeadline &&
                this.localHeadline.length > 0 &&
                this.localHeadline.length <= 100 &&
                this.safeContentLength > 0 &&
                this.safeContentLength <= 5000
            );
        },
    },

    watch: {
        modelValue: {
            immediate: true,
            handler(newVal) {
                if (newVal && this.profile) {
                    this.localHeadline = this.profile.headline || "";
                    this.localSummary = this.profile.summary || "";
                    this.previousSummary = this.localSummary;
                }
            },
        },

        localSummary(newContent) {
            const plainText = this.getPlainText(newContent);

            if (plainText.length > 5000) {
                this.$message.warning(
                    this.$trans("web_skill_ranking.admin.summary_too_long") ||
                        "Summary is too long.",
                );

                this.$nextTick(() => {
                    this.localSummary = this.previousSummary;
                });
            } else {
                this.previousSummary = newContent;
            }
        },
    },

    methods: {
        avatarBackground(profileId) {
            if (!profileId) return "#f0f0f0";

            const colors = ["#41abc4", "#aa916a", "#ff6b65"];
            const index = Math.abs(profileId) % colors.length;

            return colors[index];
        },

        getPlainText(html) {
            if (!html) return "";

            const doc = new DOMParser().parseFromString(html, "text/html");

            let text = doc.body.textContent || "";

            text = text.replace(/<\/?[^>]+(>|$)/g, "");

            return text
                .replace(/\u00a0/g, " ")
                .replace(/\n/g, "")
                .trim();
        },

        close() {
            this.$emit("update:modelValue", false);
        },

        onClosed() {
            this.localHeadline = "";
            this.localSummary = "";
            this.previousSummary = "";
        },

        save() {
            if (!this.isValid) {
                this.$message.warning(
                    this.$trans("web_skill_ranking.admin.fill_all_fields") ||
                        "Please fill all fields correctly.",
                );

                return;
            }

            this.$emit("submit", {
                headline: this.localHeadline,
                summary: this.localSummary,
            });
        },
    },
};
</script>

<style scoped>
.edit-profile-modal :deep(.el-dialog) {
    margin-top: 4vh !important;
    overflow: hidden;
}

.edit-profile-modal :deep(.el-dialog__header) {
    padding: 24px 30px;
    border-bottom: 1px solid #e5e5e5;
    margin-right: 0 !important;
}

.edit-profile-modal :deep(.el-dialog__body) {
    padding: 0;
}

.custom-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    width: 100%;
}

.profile-summary-info {
    display: flex;
    align-items: center;
    gap: 18px;
    min-width: 0;
    flex: 1;
}

.position-wrapper {
    min-width: 68px;
    width: 68px;
    height: 68px;
    border-radius: 10px;
    background-color: #41abc4;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.position-svg {
    width: 38px;
    height: 38px;
    object-fit: contain;
    filter: brightness(0) invert(1);
}

.avatar-wrapper {
    width: 68px;
    height: 68px;
    min-width: 68px;
    border-radius: 50%;
    overflow: hidden;
    background-color: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    position: relative;
}

.profile-avatar {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.cell-multiline {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
}

.font-bold {
    font-size: 22px;
    font-weight: 700;
    color: #000;
    line-height: 1.2;
}

.text-sm {
    font-size: 15px;
    color: #666;
    line-height: 1.4;
    word-break: break-word;
}

.mt-1 {
    margin-top: 4px;
}

.header-actions {
    display: flex;
    align-items: center;
    gap: 18px;
    flex-shrink: 0;
    margin-right: -28px;
}

.dialog-cancel-text {
    cursor: pointer;
    font-size: 14px;
    color: #909399;
    font-weight: 600;
    user-select: none;
    display: flex;
    align-items: center;
    transition: 0.2s ease;
}

.dialog-cancel-text:hover {
    color: #41abc4;
}

.dialog-cancel-text :deep(.el-icon) {
    margin-left: 6px;
    transform: scale(1.2);
}

.save-button {
    background-color: #41abc4 !important;
    border-color: #41abc4 !important;
    min-width: 170px;
    font-size: 16px !important;
    font-weight: 700 !important;
}

.save-button:hover {
    background-color: #3693a9 !important;
    border-color: #3693a9 !important;
}

.content-container {
    width: 100%;
    padding: 30px;
    background-color: #ededed;
    box-sizing: border-box;
    font-family: "Barlow", sans-serif;
}

.field-group {
    margin-bottom: 28px;
}

.field-label {
    display: block;
    font-weight: 700;
    font-size: 16px;
    margin-bottom: 12px;
    color: #000;
    text-align: left;
}

:deep(.el-input) {
    width: 100%;
}

:deep(.el-input__wrapper) {
    border-radius: 0 !important;
    padding: 8px 12px;
}

:deep(.el-input__wrapper.is-focus) {
    border: 2px solid #41abc4 !important;
    box-shadow: none !important;
}

:deep(.el-textarea__inner) {
    min-width: 100% !important;
    font-size: 16px !important;
    font-weight: 600 !important;
    min-height: 80px !important;
    padding: 14px !important;
    border-radius: 0 !important;
    line-height: 1.5;
    margin-left: 0px;
}

.char-counter {
    text-align: right;
    font-size: 13px !important;
    font-weight: 700 !important;
    color: #555;
    margin-top: 8px;
}

.cancel-area {
    margin-top: -100px;
    margin-right: -188px;
}

:deep(.tox-tinymce) {
    border-radius: 0 !important;
    overflow: hidden;
    border: 1px solid #dcdfe6 !important;
}

:deep(.tox .tox-toolbar),
:deep(.tox .tox-toolbar__primary),
:deep(.tox .tox-edit-area__iframe) {
    background-color: #fff !important;
}

:deep(.el-button.is-disabled),
:deep(.el-button.is-disabled:hover),
:deep(.el-button.is-disabled:focus) {
    background-color: #bababa !important;
    border-color: #bababa !important;
    color: #ffffff !important;
    cursor: not-allowed !important;
    opacity: 1 !important;
}
</style>